import { useState, useRef, useEffect, useMemo } from "react";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { PalAvatar, ElementBadges } from "@/components/PalAvatar";
import { Search, X, ChevronDown } from "lucide-react";

// Searchable Pal selector (by French or English name). value = pal index or null.
export function PalPicker({ value, onChange, placeholder = "Choisir un Pal…", testid = "pal-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = value != null ? PALS_BY_INDEX[value] : null;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = PALS_BY_INDEX;
    if (s) list = list.filter((p) => p.fr.toLowerCase().includes(s) || p.name.toLowerCase().includes(s));
    return list.slice(0, 80);
  }, [q]);

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className="w-full flex items-center gap-2.5 rounded-lg border border-border bg-[hsl(var(--surface))] px-3 py-2.5 text-left hover:border-primary/50 transition-colors"
      >
        {selected ? (
          <>
            <PalAvatar pal={selected} size={34} showElement />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{selected.fr}</div>
              <div className="text-[10px] text-muted-foreground">Puissance {selected.power}</div>
            </div>
          </>
        ) : (
          <span className="flex-1 text-sm text-muted-foreground">{placeholder}</span>
        )}
        {selected ? (
          <X
            className="w-4 h-4 text-muted-foreground hover:text-red-400 shrink-0"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            data-testid={`${testid}-clear`}
          />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border border-border bg-[hsl(var(--surface))] shadow-2xl overflow-hidden fade-up">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un Pal…"
                data-testid={`${testid}-search`}
                className="w-full rounded-md bg-black/30 border border-border pl-8 pr-2 py-1.5 text-sm outline-none focus:border-primary/50"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto scrollbar-thin">
            {results.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Aucun Pal trouvé.</div>
            ) : (
              results.map((p) => (
                <button
                  key={p.i}
                  type="button"
                  onClick={() => { onChange(p.i); setOpen(false); setQ(""); }}
                  data-testid={`${testid}-option-${p.i}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--surface-hover))] transition-colors text-left"
                >
                  <PalAvatar pal={p} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.fr}</div>
                    <ElementBadges elements={p.elements} />
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">Pw {p.power}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
